/**
 * 📊 Filter Analytics Dashboard Component
 * Visual analytics for filter usage, trends, and performance
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  Typography,
  Grid,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  FilterList,
  Visibility,
  CheckCircle,
  Category,
} from '@mui/icons-material';
import { filterManagementService } from '@/services/filterManagementService';
import {
  FilterAnalyticsResponse,
  FilterUsageStats,
  CategoryAnalytics,
  FilterAnalyticsParams,
  FilterCategory,
} from '@/types/filters';
import toast from 'react-hot-toast';

// ============================================
// Component Props
// ============================================

interface FilterAnalyticsProps {
  open: boolean;
  onClose: () => void;
  categories: FilterCategory[];
}

// ============================================
// Helper Components
// ============================================

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  subtitle?: string;
}> = ({ title, value, icon, color = 'primary', subtitle }) => (
  <Card sx={{ p: 2 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight={600}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          p: 1,
          borderRadius: 1,
          bgcolor: `${color}.lighter`,
          color: `${color}.main`,
        }}
      >
        {icon}
      </Box>
    </Stack>
  </Card>
);

// ============================================
// Main Component
// ============================================

export const FilterAnalytics: React.FC<FilterAnalyticsProps> = ({
  open,
  onClose,
  categories,
}) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<FilterAnalyticsResponse['data'] | null>(null);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // ============================================
  // Data Loading
  // ============================================

  useEffect(() => {
    if (open) {
      loadAnalytics();
    }
  }, [open, dateRange, selectedCategory]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const params: FilterAnalyticsParams = {};

      // Set date range
      const now = new Date();
      if (dateRange !== 'all') {
        const days = parseInt(dateRange.replace('d', ''));
        const fromDate = new Date(now);
        fromDate.setDate(now.getDate() - days);
        params.date_from = fromDate.toISOString();
        params.date_to = now.toISOString();
      }

      // Set category filter
      if (selectedCategory) {
        params.category_id = selectedCategory;
      }

      const response = await filterManagementService.getFilterAnalytics(params);

      if (response.success) {
        setAnalytics(response.data);
      } else {
        toast.error(response.error || 'Failed to load analytics');
      }
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || 'Unknown';
  };

  const formatPercentage = (value: number | null): string => {
    if (value === null) return 'N/A';
    return `${(value * 100).toFixed(1)}%`;
  };

  // ============================================
  // Render
  // ============================================

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Filter Analytics Dashboard</Typography>
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value as any)}
              >
                <MenuItem value="7d">Last 7 days</MenuItem>
                <MenuItem value="30d">Last 30 days</MenuItem>
                <MenuItem value="90d">Last 90 days</MenuItem>
                <MenuItem value="all">All time</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : !analytics ? (
          <Alert severity="error">Failed to load analytics data</Alert>
        ) : (
          <Stack spacing={3}>
            {/* Overview Stats */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Overview
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    title="Total Filters"
                    value={analytics.overview.total_filters}
                    icon={<FilterList />}
                    color="primary"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    title="Published"
                    value={analytics.overview.published_filters}
                    icon={<CheckCircle />}
                    color="success"
                    subtitle={`${((analytics.overview.published_filters / analytics.overview.total_filters) * 100).toFixed(0)}% of total`}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    title="Total Usage"
                    value={analytics.overview.total_usage.toLocaleString()}
                    icon={<Visibility />}
                    color="info"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard
                    title="Avg Conversion"
                    value={formatPercentage(analytics.overview.avg_conversion_rate)}
                    icon={<TrendingUp />}
                    color="warning"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Top Performing Filters */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Top Performing Filters
              </Typography>
              <Card>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Filter Name</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Usage Count</TableCell>
                        <TableCell align="right">Conversion Rate</TableCell>
                        <TableCell align="right">Trending Score</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.top_filters.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No usage data available for this period
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        analytics.top_filters.slice(0, 10).map((filter) => (
                          <TableRow key={filter.filter_id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {filter.filter_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={filter.category_name} size="small" />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                {filter.usage_count.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                                <Typography variant="body2">
                                  {formatPercentage(filter.conversion_rate)}
                                </Typography>
                                {filter.conversion_rate !== null && filter.conversion_rate > 0.1 && (
                                  <TrendingUp fontSize="small" color="success" />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell align="right">
                              {filter.trending_score !== null ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 60 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={Math.min(filter.trending_score * 10, 100)}
                                      color={filter.trending_score > 5 ? 'success' : 'warning'}
                                    />
                                  </Box>
                                  <Typography variant="caption">
                                    {filter.trending_score.toFixed(1)}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  N/A
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            </Box>

            {/* Category Breakdown */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Category Breakdown
              </Typography>
              <Grid container spacing={2}>
                {analytics.category_breakdown.length === 0 ? (
                  <Grid item xs={12}>
                    <Alert severity="info">No category data available</Alert>
                  </Grid>
                ) : (
                  analytics.category_breakdown.map((cat) => (
                    <Grid item xs={12} sm={6} md={4} key={cat.category_id}>
                      <Card sx={{ p: 2 }}>
                        <Stack spacing={1}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Category fontSize="small" color="action" />
                            <Typography variant="subtitle2">{cat.category_name}</Typography>
                          </Stack>

                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              Total Filters
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {cat.total_filters}
                            </Typography>
                          </Stack>

                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              Published
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {cat.published_filters}
                            </Typography>
                          </Stack>

                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              Total Usage
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {cat.total_usage.toLocaleString()}
                            </Typography>
                          </Stack>

                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              Avg Conversion
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                              {formatPercentage(cat.avg_conversion_rate)}
                            </Typography>
                          </Stack>

                          <LinearProgress
                            variant="determinate"
                            value={(cat.published_filters / cat.total_filters) * 100}
                            sx={{ mt: 1 }}
                          />
                        </Stack>
                      </Card>
                    </Grid>
                  ))
                )}
              </Grid>
            </Box>

            {/* Date Range Info */}
            {analytics.date_range && (
              <Alert severity="info">
                Showing data from {new Date(analytics.date_range.from).toLocaleDateString()} to{' '}
                {new Date(analytics.date_range.to).toLocaleDateString()}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={loadAnalytics} disabled={loading}>
          Refresh
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilterAnalytics;
